import { useEffect, useRef, type JSX, type ReactNode } from 'react';
import gsap from 'gsap';
import { prefersReducedMotion } from '../../preferences/reducedMotion.js';
import './PageTurnLeaf.css';

interface PageTurnLeafProps {
  /** Front-face palette. The face the viewer sees at the start of the turn. */
  from: 'village' | 'ghost';
  /** Back-face palette. The face that lands flat after the turn settles. */
  to: 'village' | 'ghost';
  /**
   * Optional content rendered inside the front face. Used so the page the
   * viewer was looking at literally turns with the leaf — the content tilts,
   * foreshortens, and slips out of view past -90° via backface-visibility.
   * If omitted, the front face renders as paper only.
   */
  frontContent?: ReactNode;
  /** Fires after the leaf has rotated and faded; used by the parent to
   *  release the transitioning state. */
  onComplete?: () => void;
}

/**
 * The recto-half page-turn leaf. A 3D element with two faces (front and
 * back) rotating around its left edge — the spine. Front face is visible
 * 0° → -90°; at -90° the leaf is edge-on and momentarily invisible; from
 * -90° → -180° the back face becomes visible and settles flat.
 *
 * The front face carries the page that's *being turned* — the lobby's
 * recto content (Wordmark, VillageIllustration, PrintedCast) is
 * passed in as `frontContent` so the eye has something to track through
 * the arc rather than watching empty paper rotate. The back face shows
 * the destination palette so the chromatic-divergence beat lands as the
 * page settles.
 *
 * Timing: 1200ms rotation eased with `power3.inOut` — slow lift, fast
 * apex, slow settle. After the rotation the leaf fades over ~180ms so the
 * meeting's recto reads through. A warm Aged-Ink drop-shadow follows the
 * leaf silhouette and breathes through the rotation (rises through the
 * first half, recedes through the second) so the lift reads physically.
 *
 * Reduced motion collapses the entire sequence to instant — `onComplete`
 * fires next tick and the leaf never renders a frame.
 */
export function PageTurnLeaf({
  from,
  to,
  frontContent,
  onComplete,
}: PageTurnLeafProps): JSX.Element {
  const leafRef = useRef<HTMLDivElement>(null);
  const frontContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reducedMotion = prefersReducedMotion();
    if (reducedMotion) {
      onComplete?.();
      return;
    }
    if (!leafRef.current) return;

    const tl = gsap.timeline({
      onComplete: () => onComplete?.(),
    });

    /* Rotation: 1200ms, power3.inOut (more pronounced slow-lift / fast-apex /
       slow-settle than the prior power2.inOut). The page feels heavier — and
       has time to *be* heavy now that content lifts away with it. */
    tl.fromTo(
      leafRef.current,
      { rotateY: 0, opacity: 1, filter: 'drop-shadow(0 0 0 oklch(22% 0.024 60 / 0))' },
      {
        rotateY: -180,
        duration: 1.2,
        ease: 'power3.inOut',
      },
      0,
    );

    /* Drop-shadow breathes through the rotation: rises through the first
       half so the lift reads physical, recedes through the second half so
       the page settles back to the desk. Aged-Ink at low alpha per the
       Warm-Shadow Rule. */
    tl.to(
      leafRef.current,
      {
        filter: 'drop-shadow(0 14px 28px oklch(22% 0.024 60 / 0.22))',
        duration: 0.6,
        ease: 'power2.out',
      },
      0,
    );
    tl.to(
      leafRef.current,
      {
        filter: 'drop-shadow(0 0 0 oklch(22% 0.024 60 / 0))',
        duration: 0.6,
        ease: 'power2.in',
      },
      0.6,
    );

    /* Hide the front content right at the apex. backface-visibility:hidden
       on the face element hides the face's own background, but children
       with their own compositing contexts (the village's filter chain,
       printed cast tooltips, etc.) can render through it in Chromium —
       you'd see the lobby's recto on the verso side after the leaf
       settles. An explicit opacity tween across the edge-on window kills
       the content reliably regardless of child compositing. */
    if (frontContentRef.current) {
      tl.to(
        frontContentRef.current,
        { opacity: 0, duration: 0.12, ease: 'power1.in' },
        0.5,
      );
    }

    /* Fade the leaf so the meeting recto reads through. Positioned at 1.25
       (after the snapshot's 0.85+0.4=1.25 fade ends) so the leaf stays
       fully opaque through the entire snapshot fade — its back face fully
       occludes the verso half once past about -170° (around 1.10s), so
       while the leaf is 100% opaque the snapshot can finish fading
       invisibly behind it. Starting the leaf fade before the snapshot is
       gone caused a tiny flash: 11% leaf transparency × 23% residual
       snapshot opacity = ~2.5% lobby-verso-content visibility right when
       the page settled. */
    tl.to(leafRef.current, { opacity: 0, duration: 0.18 }, 1.25);

    return () => {
      tl.kill();
    };
  }, [onComplete]);

  return (
    <div className="susdle-page-turn-leaf" ref={leafRef} aria-hidden>
      <div
        className="susdle-page-turn-leaf__face susdle-page-turn-leaf__face--front"
        data-pov={from}
      >
        <div className="susdle-page-turn-leaf__front-inner" ref={frontContentRef}>
          {frontContent}
        </div>
      </div>
      {/* Back face uses `from` (always warm in current callers) rather
          than `to` so the chromatic divergence does NOT happen during
          the page turn. The meeting Spread underneath defers its pov
          flip to the role-reveal moment, so the palette change is felt
          as part of "you are X" — not as a side effect of the page
          having settled. `to` is kept on the prop for documentation
          and possible future reverse-direction turns. */}
      <div
        className="susdle-page-turn-leaf__face susdle-page-turn-leaf__face--back"
        data-pov={from}
        data-to={to}
      />
    </div>
  );
}
